/** Left road section of the LUDOKA game board - Magma skin */

import type { RoadSectionProps } from '../../constants/types';
import { renderMultiplePositions } from '../basics/utils/utils';
import { LEFT_CENTER_SQUARE_BOUNDS as CENTER_SQUARE_BOUNDS } from '../basics/constants/center-square-bounds';
import type { BoardPosition } from '../../constants/positions';

/**
 * Render order for left section (Yellow)
 * Ordered left to right for proper 3D overlap
 */
const RENDER_ORDER: BoardPosition[] = [
  'YS1',
  'YC1',
  'YF1',
  'YS2',
  'YC2',
  'YF2',
  'YS3',
  'YC3',
  'YF3',
  'YS4',
  'YC4',
  'YF4',
  'YS5',
  'YC5',
  'YF5',
  'YS6',
  'YC6',
  'YF6',
  'YFINAL'
];

const SCALE_MULTIPLIER = 1.5; // Horizontal section needs larger pawns

export default function MagmaLeftRoadSection({
  viewBox = '0 0 616 268',
  className = '',
  pawns = [],
  animatingPawnIds,
  kickingPawnIds,
  rollbackPawnIds,
  onPawnClick,
  clickablePawnIds,
  pawnComponents
}: RoadSectionProps) {
  return (
    <svg
      viewBox={viewBox}
      className={className}
      overflow="visible"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Left road section with yellow final stretch"
    >
      {/* All magma visual content shifted +4.11 in Y to align with basics coordinate space */}
      <g transform="translate(0, 4.11)">
        {/* Top left corner - gray placeholder */}
        <path
          d="M21.3999 11.73H88.2399V88.57H11.3999V21.73C11.3999 16.21 15.8799 11.73 21.3999 11.73Z"
          fill="#C4C4C4"
        />

        {/* Column 1 top - dark purple frame */}
        <path
          d="M165.08 11.73H88.2402V88.57H165.08V11.73Z"
          fill="#563D4B"
          stroke="black"
          strokeMiterlimit="10"
        />

        {/* Column 1 center path - middle row (white square for pawn position) */}
        <path
          d="M150.69 94.91H102.64C98.1887 94.91 94.5801 98.5186 94.5801 102.97V151.02C94.5801 155.471 98.1887 159.08 102.64 159.08H150.69C155.141 159.08 158.75 155.471 158.75 151.02V102.97C158.75 98.5186 155.141 94.91 150.69 94.91Z"
          fill="white"
        />

        {/* Left column center row - light gray */}
        <path d="M88.2399 88.57H11.3999V165.41H88.2399V88.57Z" fill="#EFEFEF" />

        {/* Column 1: Yellow gradient center row */}
        <path
          d="M165.08 88.57H88.2402V165.41H165.08V88.57Z"
          fill="url(#mgmL_paint0)"
          stroke="black"
          strokeMiterlimit="10"
        />

        {/* Column 1 center path - middle row (second instance for layering) */}
        <path
          d="M150.69 94.91H102.64C98.1887 94.91 94.5801 98.5186 94.5801 102.97V151.02C94.5801 155.471 98.1887 159.08 102.64 159.08H150.69C155.141 159.08 158.75 155.471 158.75 151.02V102.97C158.75 98.5186 155.141 94.91 150.69 94.91Z"
          fill="white"
        />

        {/* Column 1 center path - top row (white square for pawn position) */}
        <path
          d="M150.69 18.07H102.64C98.1887 18.07 94.5801 21.6786 94.5801 26.13V74.18C94.5801 78.6314 98.1887 82.24 102.64 82.24H150.69C155.141 82.24 158.75 78.6314 158.75 74.18V26.13C158.75 21.6786 155.141 18.07 150.69 18.07Z"
          fill="white"
        />

        {/* Column 2: Dark purple frame center row */}
        <path
          d="M241.92 88.57H165.08V165.41H241.92V88.57Z"
          fill="#563D4B"
          stroke="black"
          strokeMiterlimit="10"
        />

        {/* Column 2 center path (white square for pawn position) */}
        <path
          d="M227.53 94.91H179.48C175.029 94.91 171.42 98.5186 171.42 102.97V151.02C171.42 155.471 175.029 159.08 179.48 159.08H227.53C231.981 159.08 235.59 155.471 235.59 151.02V102.97C235.59 98.5186 231.981 94.91 227.53 94.91Z"
          fill="white"
        />

        {/* Column 3: Yellow gradient center row */}
        <path
          d="M318.76 88.57H241.92V165.41H318.76V88.57Z"
          fill="url(#mgmL_paint1)"
          stroke="black"
          strokeMiterlimit="10"
        />

        {/* Column 3 center path (white square for pawn position) */}
        <path
          d="M304.37 94.91H256.32C251.868 94.91 248.26 98.5186 248.26 102.97V151.02C248.26 155.471 251.868 159.08 256.32 159.08H304.37C308.821 159.08 312.43 155.471 312.43 151.02V102.97C312.43 98.5186 308.821 94.91 304.37 94.91Z"
          fill="white"
        />

        {/* Column 4: Dark purple frame center row */}
        <path
          d="M395.61 88.57H318.77V165.41H395.61V88.57Z"
          fill="#563D4B"
          stroke="black"
          strokeMiterlimit="10"
        />

        {/* Column 4 center path (white square for pawn position) */}
        <path
          d="M381.21 94.91H333.16C328.709 94.91 325.1 98.5186 325.1 102.97V151.02C325.1 155.471 328.709 159.08 333.16 159.08H381.21C385.661 159.08 389.27 155.471 389.27 151.02V102.97C389.27 98.5186 385.661 94.91 381.21 94.91Z"
          fill="white"
        />

        {/* Column 5: Yellow gradient center row */}
        <path
          d="M472.45 88.57H395.61V165.41H472.45V88.57Z"
          fill="url(#mgmL_paint2)"
          stroke="black"
          strokeMiterlimit="10"
        />

        {/* Dark purple shadow ground - base layer */}
        <path
          d="M11.4399 228.96V254.54C11.8199 259 13.1399 265.01 17.8099 268.83C24.4199 274.23 34.0699 272.24 41.0999 271.22C59.6599 268.52 52.5099 273.71 95.6999 273.48C123.48 273.33 122.91 271.21 148.24 271.15C171.11 271.1 172.28 272.83 196.38 273.46C225.57 274.23 226.97 271.77 268.84 270.94C299.03 270.34 297.86 271.61 330.95 270.94C361.24 270.33 359.75 269.33 388.85 268.92C401.74 268.74 405.67 268.88 448.44 268.92C462.27 268.93 464.87 268.92 472.32 268.92L472.44 241.53L11.4399 228.96Z"
          fill="url(#mgmL_paint3)"
        />

        {/* Yellow gradient ground overlay */}
        <path
          d="M472.39 229.11V256.5C464.95 256.5 462.27 256.51 448.45 256.5H446.97C443.72 256.49 440.69 256.49 437.88 256.48C436.1 256.48 434.39 256.48 432.77 256.48C428.55 256.48 424.86 256.46 421.6 256.46C409.11 256.43 402.91 256.39 397.34 256.42C394.86 256.43 392.51 256.45 389.79 256.49C389.48 256.49 389.17 256.5 388.85 256.5C387.88 256.51 386.94 256.53 386.03 256.54C382.64 256.6 379.69 256.67 377.07 256.73C359.43 257.22 356.44 258 330.95 258.51C318.24 258.77 310.59 258.74 304.27 258.64C301.99 258.61 299.88 258.56 297.77 258.52C295.8 258.48 293.83 258.44 291.71 258.41C289.85 258.38 287.86 258.36 285.67 258.36C282.64 258.34 279.24 258.36 275.16 258.41C274.93 258.41 274.7 258.41 274.46 258.41C272.71 258.43 270.84 258.46 268.84 258.5C268.66 258.5 268.48 258.5 268.3 258.5C267.43 258.52 266.57 258.54 265.73 258.56C226.88 259.45 224.82 261.76 196.38 261.02C183.24 260.67 176.92 260 170.18 259.47C166.58 259.19 162.86 258.94 157.92 258.81C157.67 258.8 157.42 258.8 157.17 258.79C154.58 258.72 151.65 258.7 148.24 258.71C145.85 258.71 143.69 258.73 141.72 258.77C126.8 259.06 122.44 260.22 109 260.76H108.98C106.06 260.87 102.7 260.97 98.7199 261.01C97.8199 261.03 96.8899 261.03 95.9299 261.04H95.6999C67.2599 261.18 60.6499 258.99 54.1599 258.24C52.0099 258 49.8799 257.91 46.9699 258.12C45.6899 258.2 44.2599 258.34 42.6099 258.56C42.1299 258.62 41.6199 258.69 41.0999 258.77C38.5599 259.14 35.6899 259.64 32.7399 259.86C27.5099 260.27 22.0399 259.84 17.8099 256.39C14.5199 253.7 12.8899 249.92 12.0799 246.36C11.7399 244.87 11.5499 243.42 11.4399 242.1V216.52L201.65 221.7L241.45 222.78L306.1 224.54L328.64 225.16H328.65L362.57 226.08H362.79H362.8L429.13 227.88L466.88 228.91L472.39 229.08V229.11Z"
          fill="url(#mgmL_paint4)"
        />

        {/* Column 5 ground detail - yellow taper */}
        <path
          d="M482.09 235.48C478.94 236.01 475.65 236.21 472.39 236.38C463.33 236.84 454.46 237.09 449.37 243.52C445.97 247.81 446.58 251.94 446.97 256.5C443.72 256.49 440.69 256.49 437.88 256.48C438.16 252.1 437.1 247.53 438.84 242.08C439.72 239.34 441.18 236.78 443.08 234.42L472.39 235.22L482.09 235.48Z"
          fill="url(#mgmL_paint5)"
        />

        {/* Column 4 ground detail - yellow taper */}
        <path
          d="M397.34 256.42C394.86 256.43 392.51 256.45 389.79 256.49C389.48 256.49 389.17 256.5 388.85 256.5C387.88 256.51 386.94 256.53 386.03 256.54C383.67 251.88 380.38 247.35 374.99 243.52C358.38 231.72 331.02 233.14 315.2 245.27C309.88 249.35 306.61 254.06 304.27 258.64C301.99 258.61 299.88 258.56 297.77 258.52C299.37 253.28 301.08 246.84 305.07 241.12C307.86 237.12 311.61 233.71 315.93 230.96C319.79 228.5 324.12 226.56 328.64 225.18H328.65C337.67 222.41 347.48 221.88 355.96 223.88C358.31 224.44 360.59 225.19 362.79 226.11H362.8C366.91 227.81 370.76 230.05 374.29 232.55C386 240.83 394.12 251.84 396.18 254.75C396.58 255.31 396.96 255.87 397.34 256.42Z"
          fill="url(#mgmL_paint6)"
        />

        {/* Column 3 ground detail - yellow taper */}
        <path
          d="M275.16 258.41C274.93 258.41 274.7 258.41 274.46 258.41C272.71 258.43 270.84 258.46 268.84 258.5C268.66 258.5 268.48 258.5 268.3 258.5C262 250.82 254.05 243.02 242.41 239.2C220.2 231.91 197.34 243.32 187.57 248.19C180.58 251.68 174.92 255.54 170.18 259.47C166.58 259.19 162.86 258.94 157.92 258.81C157.67 258.8 157.42 258.8 157.17 258.79C179.97 243 178.29 242.09 186.84 237.39C189.29 236.04 197.63 231.47 207.98 228C220.77 223.73 236.62 221.15 248.18 228.5C248.5 228.7 248.81 228.91 249.11 229.12C256.91 234.57 259.61 242.9 275.16 258.39V258.41Z"
          fill="url(#mgmL_paint7)"
        />

        {/* Column 1 ground detail - yellow taper */}
        <path
          d="M108.98 260.78C106.06 260.89 102.7 260.99 98.7199 261.03C97.8199 261.05 96.8899 261.05 95.9299 261.06C89.0399 252.27 81.7399 245.24 72.1799 245.92C64.2299 246.49 59.1499 252.06 54.1599 258.26C52.0099 258.02 49.8799 257.93 46.9699 258.14C45.6899 258.22 44.2599 258.36 42.6099 258.58C49.9999 254.4 51.5499 243.31 62.3999 239.22C79.4299 232.8 100.23 250.84 102.62 252.97C105.58 255.6 107.56 258.25 108.98 260.78Z"
          fill="url(#mgmL_paint8)"
        />

        {/* Column 6: White center path squares (pawn positions) */}
        <path
          d="M458.05 94.91H410C405.549 94.91 401.94 98.5186 401.94 102.97V151.02C401.94 155.471 405.549 159.08 410 159.08H458.05C462.501 159.08 466.11 155.471 466.11 151.02V102.97C466.11 98.5186 462.501 94.91 458.05 94.91Z"
          fill="white"
        />
        <path
          d="M458.05 94.91H410C405.549 94.91 401.94 98.5186 401.94 102.97V151.02C401.94 155.471 405.549 159.08 410 159.08H458.05C462.501 159.08 466.11 155.471 466.11 151.02V102.97C466.11 98.5186 462.501 94.91 458.05 94.91Z"
          fill="white"
        />

        {/* Top row: alternating medium/light purple frame squares */}
        {/* Column 3 top - medium purple */}
        <path
          d="M318.76 11.73H241.92V88.57H318.76V11.73Z"
          fill="#7E6489"
          stroke="black"
          strokeMiterlimit="10"
        />
        {/* Column 5 top - medium purple */}
        <path
          d="M472.45 11.73H395.61V88.57H472.45V11.73Z"
          fill="#7E6489"
          stroke="black"
          strokeMiterlimit="10"
        />
        {/* Column 2 top - light purple */}
        <path
          d="M241.92 11.73H165.08V88.57H241.92V11.73Z"
          fill="#886F97"
          stroke="black"
          strokeMiterlimit="10"
        />
        {/* Column 4 top - light purple */}
        <path
          d="M395.61 11.73H318.77V88.57H395.61V11.73Z"
          fill="#886F97"
          stroke="black"
          strokeMiterlimit="10"
        />

        {/* Bottom left corner - gray placeholder */}
        <path
          d="M11.3999 165.41H88.2399V242.25H21.3999C15.8799 242.25 11.3999 237.77 11.3999 232.25V165.41Z"
          fill="#C4C4C4"
        />

        {/* Bottom row: alternating purple frame squares */}
        {/* Column 1 bottom - medium purple */}
        <path
          d="M165.08 165.41H88.2402V242.25H165.08V165.41Z"
          fill="#7E6489"
          stroke="black"
          strokeMiterlimit="10"
        />
        {/* Bottom left corner - light purple */}
        <path
          d="M11.3999 165.41H88.2399V242.25H21.3999C15.8799 242.25 11.3999 237.77 11.3999 232.25V165.41Z"
          fill="#886F97"
          stroke="black"
          strokeMiterlimit="10"
        />
        {/* Top left corner - light purple */}
        <path
          d="M21.3999 11.73H88.2399V88.57H11.3999V21.73C11.3999 16.21 15.8799 11.73 21.3999 11.73Z"
          fill="#886F97"
          stroke="black"
          strokeMiterlimit="10"
        />
        {/* Column 1 middle - medium purple */}
        <path
          d="M88.2399 88.57H11.3999V165.41H88.2399V88.57Z"
          fill="#7E6489"
          stroke="black"
          strokeMiterlimit="10"
        />
        {/* Column 3 bottom - medium purple */}
        <path
          d="M318.76 165.41H241.92V242.25H318.76V165.41Z"
          fill="#7E6489"
          stroke="black"
          strokeMiterlimit="10"
        />
        {/* Column 5 bottom - medium purple */}
        <path
          d="M472.45 165.41H395.61V242.25H472.45V165.41Z"
          fill="#7E6489"
          stroke="black"
          strokeMiterlimit="10"
        />
        {/* Column 2 bottom - light purple */}
        <path
          d="M241.92 165.41H165.08V242.25H241.92V165.41Z"
          fill="#886F97"
          stroke="black"
          strokeMiterlimit="10"
        />
        {/* Column 4 bottom - light purple */}
        <path
          d="M395.61 165.41H318.77V242.25H395.61V165.41Z"
          fill="#886F97"
          stroke="black"
          strokeMiterlimit="10"
        />

        {/* Star decoration - shadow layer + white top layer */}
        <path
          d="M212.04 195.1L205.42 183.15C204.78 182 203.14 181.95 202.44 183.07L195.21 194.66L181.8 197.26C180.5 197.51 179.96 199.06 180.8 200.07L189.59 210.53L187.92 224.09C187.76 225.4 189.06 226.4 190.29 225.91L202.95 220.78L215.33 226.56C216.53 227.12 217.88 226.19 217.79 224.87L216.82 211.24L226.14 201.25C227.04 200.28 226.57 198.71 225.29 198.39L212.03 195.1H212.04Z"
          fill="#303030"
        />
        {/* White top layer */}
        <path
          d="M212.04 193.79L205.42 181.84C204.78 180.69 203.14 180.64 202.44 181.76L195.21 193.35L181.8 195.95C180.5 196.2 179.96 197.75 180.8 198.76L189.59 209.22L187.92 222.78C187.76 224.09 189.06 225.09 190.29 224.6L202.95 219.47L215.33 225.25C216.53 225.81 217.88 224.88 217.79 223.56L216.82 209.93L226.14 199.94C227.04 198.97 226.57 197.4 225.29 197.08L212.03 193.79H212.04Z"
          fill="white"
        />

        {/* Yellow gradient triangle/arrow on right */}
        <path
          d="M587.71 126.99L472.45 242.25L473.18 11.73L587.71 126.99Z"
          fill="url(#mgmL_paint9)"
          stroke="black"
          strokeMiterlimit="10"
        />

        {/* Corner highlight detail */}
        <path
          d="M19.0701 253.41C20.3301 254.36 26.1201 258.69 32.7401 259.88C27.5101 260.29 22.0401 259.86 17.8101 256.41C14.5201 253.72 12.8901 249.94 12.0801 246.38C13.7501 248.49 15.8501 251 19.0701 253.41Z"
          fill="url(#mgmL_paint10)"
        />

        <defs>
          {/* paint0: Yellow gradient for column 1 center row */}
          <linearGradient
            id="mgmL_paint0"
            x1="88.2402"
            y1="126.99"
            x2="165.08"
            y2="126.99"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#FFCE00" />
            <stop offset="0.52" stopColor="#FFF810" />
            <stop offset="1" stopColor="#FFCE00" />
          </linearGradient>

          {/* paint1: Yellow gradient for column 3 center row */}
          <linearGradient
            id="mgmL_paint1"
            x1="241.92"
            y1="1.6711e-07"
            x2="318.77"
            y2="1.6711e-07"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#FFCE00" />
            <stop offset="0.52" stopColor="#FFF810" />
            <stop offset="1" stopColor="#FFCE00" />
          </linearGradient>

          {/* paint2: Yellow gradient for column 5 center row */}
          <linearGradient
            id="mgmL_paint2"
            x1="395.61"
            y1="1.29219e-06"
            x2="472.45"
            y2="1.29219e-06"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#FFCE00" />
            <stop offset="0.52" stopColor="#FFF810" />
            <stop offset="1" stopColor="#FFCE00" />
          </linearGradient>

          {/* paint3: Dark purple shadow for ground base */}
          <linearGradient
            id="mgmL_paint3"
            x1="241.94"
            y1="257.3"
            x2="241.94"
            y2="283.98"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#2A1C35" />
            <stop offset="1" stopColor="#000000" />
          </linearGradient>

          {/* paint4: Yellow gradient for ground overlay */}
          <linearGradient
            id="mgmL_paint4"
            x1="11.4399"
            y1="238.86"
            x2="472.39"
            y2="238.86"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#FFE33E" />
            <stop offset="1" stopColor="#FFFA5A" />
          </linearGradient>

          {/* paint5: Yellow gradient for column 5 ground taper */}
          <linearGradient
            id="mgmL_paint5"
            x1="437.81"
            y1="245.46"
            x2="482.09"
            y2="245.46"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#FFCE00" />
            <stop offset="0.52" stopColor="#FFF810" />
            <stop offset="1" stopColor="#FFCE00" />
          </linearGradient>

          {/* paint6: Yellow gradient for column 4 ground taper */}
          <linearGradient
            id="mgmL_paint6"
            x1="297.77"
            y1="240.66"
            x2="397.34"
            y2="240.66"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#FFCE00" />
            <stop offset="0.52" stopColor="#FFF810" />
            <stop offset="1" stopColor="#FFCE00" />
          </linearGradient>

          {/* paint7: Yellow gradient for column 3 ground taper */}
          <linearGradient
            id="mgmL_paint7"
            x1="157.17"
            y1="241.68"
            x2="275.16"
            y2="241.68"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#FFCE00" />
            <stop offset="0.52" stopColor="#FFF810" />
            <stop offset="1" stopColor="#FFCE00" />
          </linearGradient>

          {/* paint8: Yellow gradient for column 1 ground taper */}
          <linearGradient
            id="mgmL_paint8"
            x1="42.6099"
            y1="249.45"
            x2="108.98"
            y2="249.45"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#FFCE00" />
            <stop offset="0.52" stopColor="#FFF810" />
            <stop offset="1" stopColor="#FFCE00" />
          </linearGradient>

          {/* paint9: Yellow gradient for right triangle/arrow */}
          <linearGradient
            id="mgmL_paint9"
            x1="472.45"
            y1="126.99"
            x2="587.71"
            y2="126.99"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#FFCE00" />
            <stop offset="0.52" stopColor="#FFF810" />
            <stop offset="1" stopColor="#FFCE00" />
          </linearGradient>

          {/* paint10: Yellow gradient for corner highlight */}
          <linearGradient
            id="mgmL_paint10"
            x1="12.0801"
            y1="253.19"
            x2="32.7401"
            y2="253.19"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#FFCE00" />
            <stop offset="0.52" stopColor="#FFF810" />
            <stop offset="1" stopColor="#FFCE00" />
          </linearGradient>
        </defs>
      </g>

      {/* Pawn rendering - OUTSIDE the translated group, in basics coordinate space */}
      {renderMultiplePositions(
        RENDER_ORDER,
        pawns,
        CENTER_SQUARE_BOUNDS,
        undefined,
        SCALE_MULTIPLIER,
        animatingPawnIds,
        onPawnClick,
        clickablePawnIds,
        rollbackPawnIds,
        kickingPawnIds,
        pawnComponents
      )}
    </svg>
  );
}
